import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Stack,
  Tab,
  Tabs,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  fetchOutages,
  fetchPublicStatus,
  fetchRecentIncidents,
  fetchStaffIncidents,
  fetchStaffOutages,
  fetchStaffStatus,
  fetchStaffTelemetry,
} from './api.js';
import type {
  CheckStatus,
  IncidentSummary,
  Outage,
  PublicStatus,
  ServiceStatus,
  StaffTelemetry,
} from './types.js';
import {
  bannerSeverity,
  formatTimestamp,
  relativeTime,
  statusPaletteToken,
  statusWord,
} from './format.js';
import { buildTheme } from './theme.js';
import { UptimeBar } from './UptimeBar.js';
import { OutagesList } from './OutagesList.js';
import { IncidentsList } from './IncidentsList.js';
import { StaffPanel } from './StaffPanel.js';

const SEVERITY_COLOR: Record<IncidentSummary['severity'], 'error' | 'warning' | 'default'> = {
  critical: 'error',
  major: 'warning',
  minor: 'warning',
  maintenance: 'default',
};

function worstStatus(services: ServiceStatus[]): CheckStatus {
  if (services.some((s) => s.status === 'down')) return 'down';
  if (services.some((s) => s.status === 'degraded')) return 'degraded';
  return 'up';
}

function rollupHeadline(services: ServiceStatus[]): string {
  if (services.length === 0) return 'No services configured';
  const downCount = services.filter((s) => s.status === 'down').length;
  const degradedCount = services.filter((s) => s.status === 'degraded').length;
  if (downCount === 0 && degradedCount === 0) return 'All systems operational';
  if (downCount > 0 && degradedCount > 0) {
    return `${downCount} service${downCount === 1 ? '' : 's'} down, ${degradedCount} degraded`;
  }
  if (downCount > 0) return `${downCount} service${downCount === 1 ? '' : 's'} down`;
  return `${degradedCount} service${degradedCount === 1 ? '' : 's'} degraded`;
}

const CATEGORY_LABEL: Record<string, string> = {
  customer_apps: 'Customer apps',
  marketing_sites: 'Marketing sites',
  internal_tools: 'Internal tools',
  data_sync: 'Data sync',
};

function groupByCategory(services: ServiceStatus[]): Array<[string, ServiceStatus[]]> {
  const groups = new Map<string, ServiceStatus[]>();
  for (const s of services) {
    const list = groups.get(s.category);
    if (list) list.push(s); else groups.set(s.category, [s]);
  }
  return [...groups.entries()];
}

function StatusDot({ status }: { status: CheckStatus }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: statusPaletteToken(status),
      }}
      aria-hidden
    />
  );
}

function ServiceRow({ service, staff }: { service: ServiceStatus; staff: boolean }) {
  return (
    <Stack spacing={1} sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{service.display}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <StatusDot status={service.status} />
          <Typography variant="body2" color="text.secondary">
            {statusWord(service.status)}
          </Typography>
        </Box>
      </Box>
      <UptimeBar service={service.service} staff={staff} />
    </Stack>
  );
}

function affectedDisplay(serviceIds: string[], services: ServiceStatus[]): string {
  return serviceIds
    .map((id) => services.find((s) => s.service === id)?.display ?? id)
    .join(', ');
}

function IncidentBanner({ incidents, services }: { incidents: IncidentSummary[]; services: ServiceStatus[] }) {
  if (incidents.length === 0) return null;
  const severity = bannerSeverity(incidents);
  return (
    <Alert severity={severity} sx={{ mb: 4 }}>
      <AlertTitle>{incidents.length === 1 ? 'Active incident' : 'Active incidents'}</AlertTitle>
      <Stack spacing={1.5}>
        {incidents.map((inc) => (
          <Box key={inc.id}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={inc.severity}
                size="small"
                color={SEVERITY_COLOR[inc.severity]}
                sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{inc.title}</Typography>
              <Typography variant="body2" color="text.secondary">— {inc.current_status}</Typography>
            </Box>
            {inc.affected_services.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Affecting {affectedDisplay(inc.affected_services, services)}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" component="div">
              Started {formatTimestamp(inc.started_at)} · {relativeTime(inc.started_at)}
            </Typography>
            {inc.body && (
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                {inc.body}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Alert>
  );
}

function RollupPill({ services }: { services: ServiceStatus[] }) {
  const worst = worstStatus(services);
  const headline = rollupHeadline(services);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 3,
        py: 2,
        mt: 2,
        mb: 4,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: statusPaletteToken(worst),
          flexShrink: 0,
        }}
        aria-hidden
      />
      <Typography variant="h2" component="p" sx={{ m: 0 }}>{headline}</Typography>
    </Box>
  );
}

type TabKey = 'services' | 'outages' | 'incidents' | 'telemetry';

function TabLabelWithBadge({ label, count }: { label: string; count: number }) {
  if (count === 0) return <span>{label}</span>;
  return (
    <Badge color="primary" badgeContent={count} sx={{ '& .MuiBadge-badge': { right: -10 } }}>
      <span>{label}</span>
    </Badge>
  );
}

export function App() {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useMemo(() => buildTheme(prefersDark ? 'dark' : 'light'), [prefersDark]);

  const [data, setData] = useState<PublicStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outages, setOutages] = useState<Outage[]>([]);
  const [recentIncidents, setRecentIncidents] = useState<IncidentSummary[]>([]);
  const [staffTelemetry, setStaffTelemetry] = useState<StaffTelemetry | null>(null);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffStatus, setStaffStatus] = useState<PublicStatus | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('services');

  // Resolved service list: the staff list when the staff fetch succeeded
  // (office IP), otherwise the public list. Declared early so the effects
  // below — outages in particular — can key off it directly.
  const isStaff = staffStatus !== null;
  const view = staffStatus ?? data;

  // Stable key derived from service ids, not object identity. `view` is a
  // new object every 30s poll (data/staffStatus are both replaced wholesale),
  // so depending on `view` directly would tear down and refire the outages
  // effect every 30s instead of on its own 60s interval. This only changes
  // when the actual set of service ids changes.
  const outageKey = useMemo(
    () => (view ? view.services.map((s) => s.service).join(',') : ''),
    [view],
  );

  // Public status — every 30s.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchPublicStatus();
        if (!cancelled) {
          setData(next);
          setLastUpdated(new Date().toISOString());
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Outages — fetch per-service whenever the resolved service list changes.
  // Staff viewers get the staff-only endpoint (covers all six internal
  // services too); public viewers keep hitting the public endpoint.
  //
  // Depends on outageKey (service ids joined), not view itself — view is a
  // fresh object every 30s poll even when the service list is unchanged,
  // which would otherwise tear down and refire this effect every 30s and
  // starve its own 60s interval. Service ids are read back out of outageKey
  // rather than closing over view, so view stays out of the dependency array.
  useEffect(() => {
    if (!outageKey) return;
    const ids = outageKey.split(',');
    let cancelled = false;
    const tick = async () => {
      const results = await Promise.allSettled(
        ids.map((id) => (isStaff ? fetchStaffOutages(id) : fetchOutages(id))),
      );
      if (cancelled) return;
      const merged: Outage[] = [];
      for (const r of results) {
        if (r.status === 'fulfilled') merged.push(...r.value.outages);
      }
      setOutages(merged);
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [outageKey, isStaff]);

  // Recent incidents — open + resolved merged, every 60s. Staff viewers get
  // the unredacted staff endpoint; isStaff is in the dependency array so
  // this refetches (and switches endpoint) the moment the staff list arrives.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = isStaff ? await fetchStaffIncidents() : await fetchRecentIncidents();
        if (!cancelled) setRecentIncidents(next.incidents);
      } catch {
        // swallow — the tab shows "no incidents" if the fetch fails
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isStaff]);

  // Staff telemetry — every 30s. Drives whether the Telemetry tab appears.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await fetchStaffTelemetry();
      if (cancelled) return;
      setStaffTelemetry(next);
      setStaffLoaded(true);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Staff service list — every 30s. Null when the WAF blocks us, in which
  // case the public list is used.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await fetchStaffStatus();
      if (!cancelled) setStaffStatus(next);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const showTelemetryTab = staffLoaded && staffTelemetry !== null;

  // If the Telemetry tab disappears (e.g. WAF starts blocking), redirect
  // the user from Telemetry → Services so the page doesn't render an
  // orphaned tab.
  useEffect(() => {
    if (tab === 'telemetry' && !showTelemetryTab) setTab('services');
  }, [tab, showTelemetryTab]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
        <Container maxWidth="md" sx={{ py: { xs: 4, sm: 8 } }}>
          <Box component="header" sx={{ mb: 2 }}>
            <Typography variant="h1" component="h1">ICT Status</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Live signal for ICT-operated services.
            </Typography>
          </Box>

          {error && !view && (
            <Alert severity="error" sx={{ mb: 4 }}>
              Couldn&apos;t load status: {error}
            </Alert>
          )}

          {view && (
            <>
              <RollupPill services={view.services} />
              <IncidentBanner incidents={view.active_incidents} services={view.services} />

              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={tab}
                  onChange={(_e, v: TabKey) => setTab(v)}
                  aria-label="Status sections"
                  variant="scrollable"
                  allowScrollButtonsMobile
                  scrollButtons="auto"
                >
                  <Tab
                    value="services"
                    label={<TabLabelWithBadge label="Services" count={0} />}
                  />
                  <Tab
                    value="outages"
                    label={<TabLabelWithBadge label="Outages" count={outages.filter((o) => o.ended_at === null).length} />}
                  />
                  <Tab
                    value="incidents"
                    label={<TabLabelWithBadge label="Incidents" count={recentIncidents.filter((i) => i.current_status !== 'resolved').length} />}
                  />
                  {showTelemetryTab && (
                    <Tab value="telemetry" label="Telemetry" />
                  )}
                </Tabs>
              </Box>

              {tab === 'services' && (
                <Box component="section">
                  {groupByCategory(view.services).map(([cat, items], groupIdx) => (
                    <Box key={cat} sx={{ mt: groupIdx === 0 ? 0 : 4 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ letterSpacing: 1, fontWeight: 600 }}
                      >
                        {CATEGORY_LABEL[cat] ?? cat}
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 1 }} />
                      <Stack divider={<Divider flexItem />}>
                        {items.map((svc) => (
                          <ServiceRow key={svc.service} service={svc} staff={isStaff} />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}

              {tab === 'outages' && (
                <OutagesList outages={outages} services={view.services} />
              )}

              {tab === 'incidents' && (
                <IncidentsList incidents={recentIncidents} services={view.services} />
              )}

              {tab === 'telemetry' && staffTelemetry && (
                <StaffPanel telemetry={staffTelemetry} />
              )}
            </>
          )}

          {!view && !error && (
            <Typography color="text.secondary">Loading…</Typography>
          )}

          <Box component="footer" sx={{ mt: 8, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              {lastUpdated ? `Last updated ${relativeTime(lastUpdated)}` : 'Last updated —'}
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
